import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import Logo from "@/assets/icons/logo-steto-light-full.svg?url";
import { Images } from "@/assets/images.tsx";
import { useKeycloak } from "@/auth/KeycloakAuthState.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

const LOGO_MAX_WIDTH_PX = 300;
const COLUMN_TITLE_MAX_WIDTH_PX = 600;
const BADGE_IMG_HEIGHT_PX = 48;

const APP_STORE_URL = "https://apps.apple.com/fr/app/steto/id6448745966";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=care.steto.android";

export const PatientRedirectPage: React.FC = () => {
  const keycloak = useKeycloak(state => state.keycloak);
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.container}>
      <img className={styles.logo} src={Logo} alt="Steto" />

      <div className={styles.column}>
        <Typography variant="h6" className={styles.columnTitle}>
          {t("pages.login.patientDetails")}
        </Typography>

        <div className={styles.badges}>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={styles.badgeLink}>
            <Images.appStoreBadge alt="App Store" className={styles.badgeImg} />
            <Typography variant="caption" className={styles.badgeLabel}>
              {t("pages.login.iphone")}
            </Typography>
          </a>
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className={styles.badgeLink}>
            <Images.googlePlayBadge alt="Google Play" className={styles.badgeImg} />
            <Typography variant="bodySmall" className={styles.badgeLabel}>
              {t("pages.login.otherSmartphone")}
            </Typography>
          </a>
        </div>
      </div>

      <Button
        data-action="auth.session.logout"
        variant="contained"
        className={styles.logoutButton}
        onClick={() => keycloak.logout({ redirectUri: window.origin })}
      >
        {t("navbar.disconnect")}
      </Button>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: ${theme.palette.primary.main};
    padding: ${theme.spacing(16, 8)};
  `,
  logo: css`
    width: min(80vw, ${LOGO_MAX_WIDTH_PX}px);
    margin-bottom: ${theme.spacing(24)};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(8)};
  `,
  columnTitle: css`
    max-width: ${COLUMN_TITLE_MAX_WIDTH_PX}px;
    color: ${theme.palette.common.white};
    text-align: center;
  `,
  badges: css`
    display: flex;
    gap: ${theme.spacing(8)};
    align-items: flex-start;
  `,
  badgeLink: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(2)};
    text-decoration: none;
  `,
  badgeImg: css`
    height: ${BADGE_IMG_HEIGHT_PX}px;
    width: auto;
  `,
  badgeLabel: css`
    color: ${theme.palette.common.white};
    text-align: center;
  `,
  logoutButton: css({
    marginTop: theme.spacing(16),
    padding: theme.spacing(4, 8),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
  }),
});
